/*
  Safe compatibility wrapper.
  Add to Cart must continue even when performance tracking is unavailable.
*/
var MafraProductFormPerformanceSafe = window.MafraProductFormPerformanceSafe || {
  getApi() {
    try {
      if (typeof CartPerformance !== 'undefined') return CartPerformance;
    } catch (error) {}

    return window.CartPerformance || null;
  },

  createStartingMarker(name) {
    const api = this.getApi();
    return api && typeof api.createStartingMarker === 'function'
      ? api.createStartingMarker(name)
      : null;
  },

  measure(name, callback) {
    const api = this.getApi();

    if (api && typeof api.measure === 'function') {
      return api.measure(name, callback);
    }

    return callback();
  },

  measureFromMarker(name, marker) {
    const api = this.getApi();

    if (marker && api && typeof api.measureFromMarker === 'function') {
      api.measureFromMarker(name, marker);
    }
  },

  measureFromEvent(name, event) {
    const api = this.getApi();

    if (api && typeof api.measureFromEvent === 'function') {
      api.measureFromEvent(name, event);
    }
  },
};

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        /*
          The product form can be initialised before <cart-drawer> appears
          later in the page markup. Keep the initial lookup, but repeat it
          inside onSubmitHandler before deciding whether to redirect.
        */
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        /*
          IMPORTANT:
          Resolve the cart component again at click time. By now the full page,
          including the cart drawer near the end of <body>, has been rendered.
          This prevents Dawn from falling through to window.routes.cart_url.
        */
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        if (this.cart?.tagName.toLowerCase() === 'cart-drawer') {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname + window.location.search);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (networkResponse) => {
            const response = await networkResponse.json();
            if (
              networkResponse.ok && !response.status &&
              this.cart?.tagName?.toLowerCase() === 'cart-drawer' &&
              !response.sections?.['cart-drawer']
            ) {
              // Shopify may add successfully but return null cart-drawer HTML.
              // Refresh markup from the cart; do NOT issue a second cart/add.
              const cartUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
              const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
              if (previewThemeId) cartUrl.searchParams.set('preview_theme_id', previewThemeId);
              cartUrl.searchParams.set('sections', this.cart.getSectionsToRender().map((section) => section.id).join(','));
              const controller = new AbortController();
              const timeoutId = window.setTimeout(() => controller.abort(), 4000);
              try {
                const fallbackResponse = await fetch(cartUrl.toString(), {
                  credentials: 'same-origin', signal: controller.signal
                });
                if (fallbackResponse.ok) {
                  const fallbackSections = await fallbackResponse.json();
                  response.sections = { ...(response.sections || {}), ...fallbackSections };
                }
              } catch (error) {
                console.warn('Cart section recovery:', error);
              } finally {
                window.clearTimeout(timeoutId);
              }
            }
            return response;
          })
          .then((response) => {
            if (response.status) {
              if (
                typeof publish === 'function' &&
                typeof PUB_SUB_EVENTS !== 'undefined' &&
                PUB_SUB_EVENTS.cartError
              ) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (this.cart?.tagName?.toLowerCase() === 'cart-drawer' && !response.sections?.['cart-drawer']) {
              // The item was added; cart UI couldn't be refreshed. Navigate to
              // the real cart rather than leaving a stale drawer on screen.
              const fallbackCartUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
              const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
              if (previewThemeId) fallbackCartUrl.searchParams.set('preview_theme_id', previewThemeId);
              window.location.assign(fallbackCartUrl.toString());
              return;
            }

            const startMarker = MafraProductFormPerformanceSafe.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              const publishResult =
                typeof publish === 'function' &&
                typeof PUB_SUB_EVENTS !== 'undefined' &&
                PUB_SUB_EVENTS.cartUpdate
                  ? publish(PUB_SUB_EVENTS.cartUpdate, {
                      source: 'product-form',
                      productVariantId: formData.get('id'),
                      cartData: response,
                    })
                  : null;

              Promise.resolve(publishResult)
                .catch((error) => {
                  console.error('Cart subscriber update error:', error);
                })
                .finally(() => {
                  MafraProductFormPerformanceSafe.measureFromMarker(
                    'add:wait-for-subscribers',
                    startMarker
                  );
                });
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    MafraProductFormPerformanceSafe.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              MafraProductFormPerformanceSafe.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });

              setTimeout(() => {
                this.closest('.mafra-card-modal')
                  ?.querySelector('.mafra-card-modal__close')
                  ?.click();
              });
            }
          })
          .catch((e) => {
            console.error('Add to cart failed:', e);
            // This was a transport error, not an out-of-stock state. Allow retry.
            this.error = false;
            this.handleErrorMessage(
              window.cartStrings?.error || 'Could not confirm this update. Check your cart before trying again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            MafraProductFormPerformanceSafe.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
